import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const CapabilityContext = createContext(null);

export const useCapability = () => {
  const context = useContext(CapabilityContext);
  if (!context) {
    throw new Error('useCapability must be used within CapabilityProvider');
  }
  return context;
};

export const CapabilityProvider = ({ children }) => {
  const { api, token } = useAuth();
  const [capabilities, setCapabilities] = useState([]);
  const [instances, setInstances] = useState([]);
  const [activeInstance, setActiveInstance] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCapabilities = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await api.get('/capabilities/registry');
      setCapabilities(response.data);
    } catch (error) {
      console.error('Failed to fetch capabilities:', error);
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  const fetchInstances = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.get('/capabilities/instances');
      setInstances(response.data);
    } catch (error) {
      console.error('Failed to fetch instances:', error);
    }
  }, [api, token]);

  useEffect(() => {
    if (token) {
      fetchCapabilities();
      fetchInstances();
    }
  }, [token, fetchCapabilities, fetchInstances]);

  const createInstance = async (capabilityId) => {
    const response = await api.post('/capabilities/instances', { capability_id: capabilityId });
    await fetchInstances();
    return response.data;
  };

  const updateInstanceInputs = async (instanceId, inputs) => {
    const response = await api.patch(`/capabilities/instances/${instanceId}/inputs`, { inputs });
    await fetchInstances();
    return response.data;
  };

  const transitionInstance = async (instanceId, newState, message = '') => {
    const response = await api.post(`/capabilities/instances/${instanceId}/transition`, {
      new_state: newState,
      message
    });
    await fetchInstances();
    return response.data;
  };

  const archiveInstance = async (instanceId) => {
    await api.delete(`/capabilities/instances/${instanceId}`);
    await fetchInstances();
    if (activeInstance?.id === instanceId) {
      setActiveInstance(null);
    }
  };

  const enableCapability = async (capabilityId) => {
    await api.post(`/capabilities/registry/${capabilityId}/enable`);
    await fetchCapabilities();
  };

  const disableCapability = async (capabilityId) => {
    await api.post(`/capabilities/registry/${capabilityId}/disable`);
    await fetchCapabilities();
  };

  return (
    <CapabilityContext.Provider
      value={{
        capabilities,
        instances,
        activeInstance,
        setActiveInstance,
        loading,
        createInstance,
        updateInstanceInputs,
        transitionInstance,
        archiveInstance,
        enableCapability,
        disableCapability,
        refresh: () => { fetchCapabilities(); fetchInstances(); }
      }}
    >
      {children}
    </CapabilityContext.Provider>
  );
};
